import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useWorkflows } from "../hooks/useWorkflows";

const STATUS_CLASSES = {
    queued:             "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
    running:            "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    succeeded:          "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    failed:             "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    partial:            "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
    waiting:            "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300",
    awaiting_approval:  "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
};

const STEP_STATUS_CLASSES = {
    queued: "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700 text-yellow-700 dark:text-yellow-400",
    running: "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-400",
    succeeded: "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 text-green-700 dark:text-green-400",
    failed: "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 text-red-700 dark:text-red-400",
    skipped: "bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400"
};

const STEP_ICONS = {
    succeeded: (
        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
    ),
    failed: (
        <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
    ),
    skipped: (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
        </svg>
    ),
    running: (
        <svg className="w-4 h-4 text-blue-500 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
    ),
    queued: (
        <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    )
};

function StatusBadge({ status }) {
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${STATUS_CLASSES[status] || "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"}`}>
            {status}
        </span>
    );
}

function formatDuration(startedAt, finishedAt) {
    if (!startedAt || !finishedAt) return null;
    const ms = new Date(finishedAt) - new Date(startedAt);
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
}

function StepTrace({ steps }) {
    const [expanded, setExpanded] = useState(null);

    if (!steps || steps.length === 0) return <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">No step data</p>;

    return (
        <div className="space-y-2">
            {steps.map((step, i) => (
                <div key={step._id || i} className={`rounded-lg border-[1px] px-3 py-2.5 ${STEP_STATUS_CLASSES[step.status] || "bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-600"}`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="w-5 h-5 flex items-center justify-center shrink-0">
                                {STEP_ICONS[step.status] || STEP_ICONS.queued}
                            </div>
                            <span className="text-sm font-medium">{step.stepName || step.stepType}</span>
                            <span className="text-xs opacity-60">{step.stepType}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            {step.latencyMs && (
                                <span className="text-xs opacity-60">{step.latencyMs}ms</span>
                            )}
                            {step.costCents > 0 && (
                                <span className="text-xs opacity-60">${(step.costCents / 100).toFixed(4)}</span>
                            )}
                            {(step.output || step.error) && (
                                <button
                                    onClick={() => setExpanded(expanded === i ? null : i)}
                                    className="text-xs underline opacity-60 hover:opacity-100 transition-opacity"
                                >
                                    {expanded === i ? "hide" : "details"}
                                </button>
                            )}
                        </div>
                    </div>

                    {step.retriedCount > 0 && (
                        <p className="text-xs opacity-60 mt-1 ml-7">Retried {step.retriedCount}×</p>
                    )}

                    {expanded === i && (
                        <div className="mt-2 ml-7 space-y-1.5">
                            {step.error && (
                                <div className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded px-2 py-1.5 text-xs font-mono break-all">
                                    {step.error}
                                </div>
                            )}
                            {step.output && Object.keys(step.output).length > 0 && (
                                <pre className="bg-black/5 dark:bg-white/5 rounded px-2 py-1.5 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all">
                                    {JSON.stringify(step.output, null, 2)}
                                </pre>
                            )}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

function ApprovalPanel({ runId, onResolved }) {
    const { listApprovals, resolveApproval } = useWorkflows();
    const [approvals, setApprovals] = useState([]);
    const [resolving, setResolving] = useState(null);
    const [comment, setComment] = useState("");

    useEffect(() => {
        listApprovals(runId).then(data => setApprovals(Array.isArray(data) ? data : []));
    }, [runId]); // eslint-disable-line

    const pending = approvals.filter(a => a.status === "pending");
    if (!pending.length) return null;

    const handleAction = async (approval, action) => {
        setResolving(approval._id + action);
        try {
            await resolveApproval(approval._id, action, undefined, comment);
            onResolved?.();
            setApprovals(prev => prev.map(a => a._id === approval._id ? { ...a, status: action === "approve" ? "approved" : "rejected" } : a));
        } catch (e) {
            console.error("Failed to resolve approval", e);
        }
        setResolving(null);
    };

    return (
        <div className="mt-2 space-y-3">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Pending Approvals</p>
            {pending.map(approval => (
                <div key={approval._id} className="border-[1px] border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 space-y-2">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{approval.prompt}</p>
                    {approval.previewData && Object.keys(approval.previewData).length > 0 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                            {Object.entries(approval.previewData).map(([k, v]) => (
                                <div key={k}><span className="font-semibold">{k}:</span> {String(v)}</div>
                            ))}
                        </div>
                    )}
                    <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">Expires: {new Date(approval.expiresAt).toLocaleString()}</p>
                    <input
                        type="text"
                        placeholder="Optional comment…"
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                        className="w-full text-xs border-[1px] dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-[#181d33] dark:text-white"
                    />
                    <div className="flex gap-2">
                        <button
                            onClick={() => handleAction(approval, "approve")}
                            disabled={!!resolving}
                            className="flex-1 py-1.5 rounded text-sm font-semibold bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                        >
                            {resolving === approval._id + "approve" ? "…" : "✓ Approve"}
                        </button>
                        <button
                            onClick={() => handleAction(approval, "reject")}
                            disabled={!!resolving}
                            className="flex-1 py-1.5 rounded text-sm font-semibold bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                        >
                            {resolving === approval._id + "reject" ? "…" : "✗ Reject"}
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}

function RunDetailModal({ runId, onClose }) {
    const { getRun } = useWorkflows();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getRun(runId).then(d => { setData(d); setLoading(false); });
    }, [runId]); // eslint-disable-line

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
            <div className="bg-white dark:bg-[#23263a] rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col font-Rajdhani">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700 shrink-0">
                    <div>
                        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Run Trace</h2>
                        {data?.run && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500 mt-0.5 font-mono">{data.run._id}</p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-4 dark:text-white">
                    {loading ? (
                        <div className="flex items-center justify-center py-10 text-gray-400 dark:text-gray-500 text-sm gap-2">
                            <div className="animate-spin w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full" />
                            Loading trace…
                        </div>
                    ) : !data ? (
                        <p className="text-sm text-red-500 dark:text-red-400">Failed to load run data</p>
                    ) : (
                        <>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <StatusBadge status={data.run.status} />
                                    {data.run.dryRun && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                                            Dry Run
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">
                                    <span>{formatDuration(data.run.startedAt, data.run.finishedAt)}</span>
                                    {data.run.totalCostCents > 0 && (
                                        <span>${(data.run.totalCostCents / 100).toFixed(4)} total</span>
                                    )}
                                </div>
                            </div>

                            {data.run.error && (
                                <div className="bg-red-50 dark:bg-red-900/20 border-[1px] border-red-200 dark:border-red-800 rounded-lg px-3 py-2.5 text-sm text-red-700 dark:text-red-400">
                                    {data.run.error}
                                </div>
                            )}

                            <div>
                                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Steps</p>
                                <StepTrace steps={data.steps} />
                            </div>

                            {data.run.status === "awaiting_approval" && (
                                <ApprovalPanel
                                    runId={runId}
                                    onResolved={() => getRun(runId).then(d => setData(d))}
                                />
                            )}

                            {data.run.status === "waiting" && (
                                <div className="bg-sky-50 dark:bg-sky-900/20 border-[1px] border-sky-200 dark:border-sky-700 rounded-lg p-3 text-sm text-sky-700 dark:text-sky-400">
                                    ⏸ Workflow is paused. It will resume automatically at the scheduled time.
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function RunRow({ run, onClick }) {
    const duration = formatDuration(run.startedAt, run.finishedAt);
    return (
        <tr
            className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
            onClick={onClick}
        >
            <td className="px-4 py-3">
                <StatusBadge status={run.status} />
                {run.dryRun && (
                    <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                        test
                    </span>
                )}
            </td>
            <td className="px-4 py-3">
                <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                    {String(run.submissionId).slice(-8)}
                </span>
            </td>
            <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                {run.triggeredBy || "—"}
            </td>
            <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                {duration || "—"}
            </td>
            <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                {run.totalCostCents > 0 ? `$${(run.totalCostCents / 100).toFixed(4)}` : "—"}
            </td>
            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">
                {run.startedAt ? new Date(run.startedAt).toLocaleString() : "—"}
            </td>
        </tr>
    );
}

export default function WorkflowRuns() {
    const { formId, workflowId } = useParams();
    const navigate = useNavigate();
    const { listRuns, getWorkflow, loading } = useWorkflows();
    const [runs, setRuns] = useState([]);
    const [workflow, setWorkflow] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [selectedRunId, setSelectedRunId] = useState(null);

    const load = useCallback(async (p = 1) => {
        const [wf, runsData] = await Promise.all([
            p === 1 ? getWorkflow(workflowId) : Promise.resolve(workflow),
            listRuns(workflowId, p)
        ]);
        if (wf && p === 1) setWorkflow(wf);
        if (runsData) {
            const items = runsData.runs || runsData;
            if (p === 1) setRuns(items);
            else setRuns(prev => [...prev, ...items]);
            setHasMore(runsData.hasMore || false);
        }
    }, [workflowId]); // eslint-disable-line

    useEffect(() => { load(1); }, [load]);

    const loadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        load(nextPage);
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#181d33] font-Rajdhani dark:text-white">
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(`/form-builder/${formId}/workflows/${workflowId}`)}
                            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                                {workflow ? `${workflow.name} — Runs` : "Run History"}
                            </h1>
                            <p className="text-base text-gray-500 dark:text-gray-400 mt-0.5">Execution log for this workflow</p>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate(`/form-builder/${formId}/workflows`)}
                        className="text-sm text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                    >
                        All workflows
                    </button>
                </div>

                {/* Table */}
                {loading && runs.length === 0 ? (
                    <div className="text-center py-16 text-gray-400 dark:text-gray-500 text-sm flex items-center justify-center gap-2">
                        <div className="animate-spin w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full" />
                        Loading runs…
                    </div>
                ) : runs.length === 0 ? (
                    <div className="text-center py-16">
                        <div className="w-14 h-14 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center mx-auto mb-4">
                            <svg className="w-7 h-7 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                        </div>
                        <p className="text-base text-gray-500 dark:text-gray-400">No runs yet. This workflow hasn't been triggered.</p>
                    </div>
                ) : (
                    <div className="bg-white dark:bg-[#23263a] rounded-xl border-[1px] border-gray-200 dark:border-gray-700 overflow-hidden">
                        <table className="w-full text-sm dark:text-gray-200">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Submission</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Triggered by</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Duration</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Cost</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Started</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {runs.map(run => (
                                    <RunRow
                                        key={run._id}
                                        run={run}
                                        onClick={() => setSelectedRunId(run._id)}
                                    />
                                ))}
                            </tbody>
                        </table>

                        {hasMore && (
                            <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 text-center">
                                <button
                                    onClick={loadMore}
                                    disabled={loading}
                                    className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium disabled:opacity-50"
                                >
                                    {loading ? "Loading…" : "Load more"}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {selectedRunId && (
                <RunDetailModal
                    runId={selectedRunId}
                    onClose={() => setSelectedRunId(null)}
                />
            )}
        </div>
    );
}
